// frontend/src/components/auth/OnboardingGuard.tsx
/**
 * OnboardingGuard - Redirects users to onboarding if they haven't completed it,
 * but always allows the main dashboard so they can explore first.
 */

import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/components/providers/auth-provider'

interface OnboardingGuardProps {
    children: React.ReactNode
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
    const { user, isLoading } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
        if (isLoading || !user) return

        const path = location.pathname
        const hasCompletedOnboarding = Boolean(user.onboarding_completed)

        // If onboarding not complete, decide what to do based on path
        if (!hasCompletedOnboarding) {
            const isAppDashboard = path.startsWith('/app/dashboard')
            const isEnterpriseDashboard = path.startsWith('/enterprise/dashboard')

            // ✅ Allow dashboards even if onboarding isn't complete
            if (isAppDashboard || isEnterpriseDashboard) {
                return
            }

            // For other /app or /enterprise routes, send them to onboarding
            if (path.startsWith('/app') || path.startsWith('/enterprise')) {
                navigate('/onboarding', { replace: true })
            }
        }
    }, [user, isLoading, location.pathname, navigate])

    // While auth is loading, show a simple spinner
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
        )
    }

    // ✅ Always render children here; redirects are handled in the effect
    return <>{children}</>
}
